/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Listbox} from '@headlessui/react'
import {ArrowsUpDownIcon} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import {useTranslation} from 'react-i18next'
import {createPortal} from 'react-dom'
import {useDebouncedCallback} from 'utils/hooks/useDebounce'
import UiSelectWithGraphicsOptionsList from './UiSelectWithGraphicsComponents/UiSelectWithGraphicsOptionsList'
import {
    uiFormControlDisabledClassName,
    uiFormControlFocusClassName,
    uiFormControlSurfaceClassName,
} from './UiFormControlStyles'

export interface UiSelectOption {
    label: string
    description?: string
    value: string | number,
	position?: number,
	category?: string,
	className?: string
}

interface UiSelectWithGraphicsProps {
    options?: UiSelectOption[]
    groupedOptions?: { groupLabel: string; options: UiSelectOption[] }[]
    selected: UiSelectOption['value'] | null
    setSelected: (value: UiSelectOption['value'] | null) => void
    label: string
    required?: boolean
    disabled?: boolean
    dropdownDirection?: 'up' | 'down'
    className?: string
    title?: string
    debounceMs?: number
    /** Minimum dropdown panel width in px (defaults to anchor width). */
    dropdownMinWidth?: number
    /** Allow option labels to wrap instead of truncating. */
    wrapOptionLabels?: boolean
}

const UiSelectWithGraphics = (props: UiSelectWithGraphicsProps) => {
    const { t: translate } = useTranslation()
    const dropdownDirection = props.dropdownDirection ?? 'down'
    const debounceMs = props.debounceMs ?? 0

    const debouncedSetSelected = useDebouncedCallback(
        (value: UiSelectOption['value'] | null) => props.setSelected(value),
        debounceMs
    )

    const handleChange = (newOption: UiSelectOption) => {
        if (debounceMs <= 0) {
            props.setSelected(newOption.value)
            return
        }
        debouncedSetSelected(newOption.value)
    }

    const flatOptions: UiSelectOption[] = useMemo(() => {
        if (Array.isArray(props.options) && props.options.length > 0)
            return props.options
        const groups = props.groupedOptions ?? []
        return groups.flatMap((g) => g.options)
    }, [props.options, props.groupedOptions])

    const foundOption = flatOptions.find(
        (optionItem) =>
            String(optionItem.value ?? '') === String(props.selected ?? '')
    )

    const fallbackOption: UiSelectOption = {
        label: translate('Select'),
        description: '',
        value: '',
    }

    const selectedOption = foundOption || fallbackOption

    const anchorRef = useRef<HTMLDivElement>(null)
    const optionsRef = useRef<HTMLDivElement>(null)
    const [, forceUpdate] = useState(0)

    useEffect(() => {
        const handler = (event: Event) => {
            const target = event.target
            if (target instanceof Node && optionsRef.current?.contains(target)) {
                return
            }
            forceUpdate((prev) => prev + 1)
        }
        window.addEventListener('resize', handler)
        window.addEventListener('scroll', handler, true)
        return () => {
            window.removeEventListener('resize', handler)
            window.removeEventListener('scroll', handler, true)
        }
    }, [])

    const handleOptionsWheel: React.WheelEventHandler<HTMLDivElement> = (event) => {
        const list = event.currentTarget
        const delta = event.deltaY
        const atTop = list.scrollTop <= 0
        const atBottom = Math.ceil(list.scrollTop + list.clientHeight) >= list.scrollHeight

        // Keep wheel scrolling inside the dropdown and prevent scroll chaining to parent modal/body.
        if ((delta < 0 && atTop) || (delta > 0 && atBottom)) {
            if (event.cancelable) {
                event.preventDefault()
            }
        }
        event.stopPropagation()
    }

    const computePosition = (): { panel: React.CSSProperties; maxHeight: number } => {
        const anchor = anchorRef.current
        if (!anchor) return { panel: { position: 'fixed', zIndex: 9999 }, maxHeight: 240 }

        const rect = anchor.getBoundingClientRect()
        const viewportMargin = 8
        const maxPanelHeight = 240
        const requestedWidth = Math.max(rect.width, props.dropdownMinWidth ?? 0)
        const width = Math.min(requestedWidth, window.innerWidth - viewportMargin * 2)
        const left = Math.max(
            viewportMargin,
            Math.min(rect.left, window.innerWidth - width - viewportMargin)
        )
        const availableBelow = window.innerHeight - rect.bottom - viewportMargin
        const availableAbove = rect.top - viewportMargin
        const openUp =
            dropdownDirection === 'up' ||
            (dropdownDirection === 'down' &&
                availableBelow < maxPanelHeight &&
                availableAbove > availableBelow)
        const availableHeight = openUp ? availableAbove : availableBelow
        const maxHeight = Math.max(96, Math.min(maxPanelHeight, availableHeight))

        if (openUp) {
            return {
                panel: {
                    position: 'fixed',
                    left,
                    bottom: window.innerHeight - rect.top + 4,
                    width,
                    zIndex: 9999,
                },
                maxHeight,
            }
        }

        return {
            panel: {
                position: 'fixed',
                left,
                top: rect.bottom + 4,
                width,
                zIndex: 9999,
            },
            maxHeight,
        }
    }

    return (
        <Listbox
            value={selectedOption}
            onChange={handleChange}
            by={(a, b) => String(a?.value ?? '') === String(b?.value ?? '')}
        >
            {({ open }) => {
                const dropdownPosition = computePosition()

                return <div
                    className={clsx(
                        'UiSelectWithGraphicsListbox text-xs',
                        props.className
                    )}
                >
                    {props.label && (
                        <Listbox.Label
                            className={clsx(
                                'font-medium text-gray-700 dark:text-gray-200 text-sm pb-2 block select-none'
                            )}
                        >
                            {props.label}
                            {props.required && <em className="ml-1 text-red-500">*</em>}
                        </Listbox.Label>
                    )}

                    <div
                        title={props.title}
                        ref={anchorRef}
                        className={clsx('UiSelectWithGraphics relative')}
                    >
                        <Listbox.Button
                            disabled={props.disabled}
                            title={props.title || selectedOption.label}
                            className={clsx(
                                'relative pl-3 pr-10 py-2 text-left select-none w-full h-10 rounded-lg border shadow-sm transition-colors',
                                props.disabled
                                    ? uiFormControlDisabledClassName
                                    : clsx(
                                          uiFormControlSurfaceClassName,
                                          uiFormControlFocusClassName,
                                          'cursor-pointer'
                                      )
                            )}
                        >
                            <span className={clsx('block truncate select-none')}>
                                {selectedOption.label}
                            </span>
                            <span
                                className={clsx(
                                    'absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none'
                                )}
                            >
                                <ArrowsUpDownIcon
                                    className={clsx(
                                        'h-5 w-5',
                                        props.disabled
                                            ? 'text-gray-500 dark:text-gray-500'
                                            : 'text-gray-800 dark:text-gray-200'
                                    )}
                                />
                            </span>
                        </Listbox.Button>

                        {open &&
                            createPortal(
                                <div style={dropdownPosition.panel}>
                                    <Listbox.Options
                                        ref={optionsRef}
                                        onWheel={handleOptionsWheel}
                                        style={{ maxHeight: dropdownPosition.maxHeight }}
                                        className={clsx(
                                            'custom-scrollbar w-full bg-white text-gray-900 shadow-xl max-h-60 overflow-y-auto overscroll-contain rounded-md py-1 ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-900 dark:text-gray-100 dark:ring-gray-700 text-xs'
                                        )}
                                    >
                                        <UiSelectWithGraphicsOptionsList
                                            groupedOptions={
                                                props.groupedOptions
                                            }
                                            options={flatOptions}
                                            wrapOptionLabels={props.wrapOptionLabels}
                                        />
                                    </Listbox.Options>
                                </div>,
                                document.body
                            )}
                    </div>
                </div>
            }}
        </Listbox>
    )
}

export default UiSelectWithGraphics
